import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"

export type FormValues = Record<string, string | boolean>

export interface FieldDef {
  key: string
  label: string
  kind: "text" | "number" | "time" | "date" | "textarea" | "switch" | "select"
  required?: boolean
  placeholder?: string
  step?: string
  /** Nur kind="select" */
  options?: { value: string; label: string }[]
  /** Label der Leer-Option; ohne Angabe ist das Select pflichtig ohne Leer-Option */
  emptyOption?: string
}

interface EntityFormSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Stabiler Schlüssel für die Breiten-Persistenz (pro Entität vergeben). */
  sheetKey?: string
  title: string
  description?: string
  fields: FieldDef[]
  initialValues: FormValues
  pending: boolean
  onSubmit: (values: FormValues) => void
}

/** Config-getriebener CRUD-Sheet fuer die Stammdaten-Grids (Admin-Seite). */
export function EntityFormSheet({
  open,
  onOpenChange,
  sheetKey = "entity-form",
  title,
  description,
  fields,
  initialValues,
  pending,
  onSubmit,
}: EntityFormSheetProps) {
  const [values, setValues] = useState<FormValues>(initialValues)

  useEffect(() => {
    if (open) setValues(initialValues)
    // initialValues ist bei jedem Oeffnen ein frisches Objekt des Aufrufers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const valid = fields.every((f) => {
    if (!f.required) return true
    const v = values[f.key]
    return typeof v === "boolean" || (typeof v === "string" && v.trim() !== "")
  })

  function set(key: string, value: string | boolean) {
    setValues((v) => ({ ...v, [key]: value }))
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey={sheetKey} className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <div className="grid gap-4 py-2">
          {fields.map((f) => {
            const id = `field-${f.key}`
            if (f.kind === "switch") {
              return (
                <div key={f.key} className="flex items-center justify-between gap-2">
                  <Label htmlFor={id}>{f.label}</Label>
                  <Switch
                    id={id}
                    checked={values[f.key] === true}
                    onCheckedChange={(c) => set(f.key, c)}
                  />
                </div>
              )
            }
            if (f.kind === "select") {
              return (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={id}>{f.label}</Label>
                  <Select
                    id={id}
                    value={String(values[f.key] ?? "")}
                    onChange={(e) => set(f.key, e.target.value)}
                  >
                    {f.emptyOption !== undefined && <option value="">{f.emptyOption}</option>}
                    {f.options?.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>
              )
            }
            if (f.kind === "textarea") {
              return (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={id}>{f.label}</Label>
                  <Textarea
                    id={id}
                    placeholder={f.placeholder}
                    value={String(values[f.key] ?? "")}
                    onChange={(e) => set(f.key, e.target.value)}
                  />
                </div>
              )
            }
            return (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={id}>{f.label}</Label>
                <Input
                  id={id}
                  type={f.kind === "text" ? "text" : f.kind}
                  step={f.step}
                  placeholder={f.placeholder}
                  value={String(values[f.key] ?? "")}
                  onChange={(e) => set(f.key, e.target.value)}
                />
              </div>
            )
          })}
        </div>
        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!valid || pending} onClick={() => onSubmit(values)}>
            Speichern
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
